import { useId } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
const EASE = [0.16, 1, 0.3, 1]
const SEAL_SIZE = 64
const CENTER = SEAL_SIZE / 2
function rosettePath(radius, amplitude, lobes, phase) {
  const steps = lobes * 12
  let path = ''
  for (let step = 0; step <= steps; step += 1) {
    const angle = (step / steps) * Math.PI * 2
    const r = radius + amplitude * Math.cos(lobes * angle + phase)
    const x = CENTER + r * Math.cos(angle)
    const y = CENTER + r * Math.sin(angle)
    path += `${step === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)} `
  }
  return `${path}Z`
}
/** Polygon's mark, drawn on a 24-unit grid. */
const POLYGON_MARK =
  'm17.82 16.342 5.692-3.287A.98.98 0 0 0 24 12.21V5.635a.98.98 0 0 0-.488-.846l-5.693-3.286a.98.98 0 0 0-.977 0L11.15 4.789a.98.98 0 0 0-.489.846v11.747L6.67 19.686l-3.992-2.304v-4.61l3.992-2.304 2.633 1.52V8.896L7.158 7.658a.98.98 0 0 0-.977 0L.488 10.945a.98.98 0 0 0-.488.846v6.573a.98.98 0 0 0 .488.847l5.693 3.286a.981.981 0 0 0 .977 0l5.692-3.286a.98.98 0 0 0 .489-.846V6.618l.072-.041 3.92-2.263 3.99 2.305v4.609l-3.99 2.304-2.63-1.517v3.092l2.14 1.236a.981.981 0 0 0 .978 0v-.001Z'

const ROSETTE_A = rosettePath(23.5, 3, 20, 0)
const ROSETTE_B = rosettePath(23.5, 3, 20, Math.PI)
function SealEmblem({ className, ready }) {
  const reduceMotion = useReducedMotion()
  const gradientId = `seal-foil-${useId().replace(/:/g, '')}`
  const engrave = {
    hidden: { pathLength: reduceMotion ? 1 : 0, opacity: reduceMotion ? 1 : 0 },
    show: {
      pathLength: 1,
      opacity: 1,
      transition: { duration: reduceMotion ? 0 : 0.8, delay: reduceMotion ? 0 : 0.1, ease: EASE },
    },
  }
  const mark = {
    hidden: { opacity: reduceMotion ? 1 : 0 },
    show: {
      opacity: 1,
      transition: { duration: reduceMotion ? 0 : 0.45, delay: reduceMotion ? 0 : 0.5, ease: EASE },
    },
  }
  const controlled =
    ready === void 0 ? {} : { initial: 'hidden', animate: ready ? 'show' : 'hidden' }
  return (
    <motion.svg
      viewBox={`0 0 ${SEAL_SIZE} ${SEAL_SIZE}`}
      className={className}
      fill="none"
      aria-hidden="true"
      focusable="false"
      {...controlled}
    >
      <defs>
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1="4"
          y1="60"
          x2="60"
          y2="4"
        >
          <stop offset="0" stopColor="#8FD8E6" />
          <stop offset="0.5" stopColor="#B49CF5" />
          <stop offset="1" stopColor="#8247E5" />
        </linearGradient>
      </defs>

      <motion.circle
        cx={CENTER}
        cy={CENTER}
        r="30"
        stroke={`url(#${gradientId})`}
        strokeWidth="0.9"
        variants={engrave}
      />
      <motion.path
        d={ROSETTE_A}
        stroke={`url(#${gradientId})`}
        strokeWidth="0.7"
        strokeLinejoin="round"
        variants={engrave}
      />
      <motion.path
        d={ROSETTE_B}
        stroke={`url(#${gradientId})`}
        strokeWidth="0.7"
        strokeLinejoin="round"
        variants={engrave}
      />
      <motion.circle
        cx={CENTER}
        cy={CENTER}
        r="17.5"
        stroke={`url(#${gradientId})`}
        strokeWidth="0.9"
        variants={engrave}
      />
      {/* Polygon mark, engraved at the heart of the seal. */}
      <motion.path
        d={POLYGON_MARK}
        fill="#EFEBE4"
        transform="translate(21.5 21.5) scale(0.88)"
        variants={mark}
      />
      {/* Small confirmation tick on the rim: the record was found on-chain. */}
      <motion.g variants={mark}>
        <circle cx="49" cy="49" r="7.5" fill="#121018" stroke="#19C670" strokeWidth="1.2" />
        <path
          d="M45.6 49.2 L48.1 51.7 L52.6 46.6"
          stroke="#19C670"
          strokeWidth="1.5"
          strokeLinecap="square"
          strokeLinejoin="miter"
        />
      </motion.g>
    </motion.svg>
  )
}
/** Polygon's mark on its own, for compact "on-chain" labels. */
function PolygonMark({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d={POLYGON_MARK} />
    </svg>
  )
}

export { SealEmblem as default, PolygonMark }
